'use client';

import { useRef, useEffect } from 'react';

interface GenericVideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  className?: string;
  onTimeUpdate?: (currentTime: number) => void;
  onEnded?: () => void;
}

/**
 * Generic HTML5 video player for direct MP4 URLs (like Rendi previews)
 * Works with any video source that MuxPlayer doesn't support
 */
export function GenericVideoPlayer({
  src,
  autoPlay = false,
  controls = true,
  loop = false,
  muted = false,
  className = '',
  onTimeUpdate,
  onEnded,
}: GenericVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (onTimeUpdate) {
        onTimeUpdate(video.currentTime);
      }
    };

    const handleEnded = () => {
      if (onEnded) {
        onEnded();
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [onTimeUpdate, onEnded]);

  return (
    <video
      ref={videoRef}
      src={src}
      autoPlay={autoPlay}
      controls={controls}
      loop={loop}
      muted={muted}
      playsInline
      preload="metadata"
      className={className}
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
      }}
    >
      Your browser does not support the video tag.
    </video>
  );
}
